import React, {
  type ComponentType,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import styles from './RangeSlider.module.css'
import { Keys } from '../Popup'
import { LayoutCluster, LayoutStack } from '../layouts'
// eslint-disable-next-line import/exports-last
export type SliderRewardProps = {
  percent: number
  value: number
}
type RangeSliderProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> & {
  'className'?: string
  'trackClassName'?: string
  'trackFilledClassName'?: string
  'thumbClassName'?: string
  'onChange'?: React.ChangeEventHandler<HTMLInputElement>
  'disabled'?: boolean
  'aria-label'?: string
  'aria-labelledby'?: string
  'children'?: ComponentType<SliderRewardProps>
  'scaleFormat'?: (value: number) => string
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function getPercent(value: number, min: number, max: number) {
  if (max === min) return 0
  return ((value - min) / (max - min)) * 100
}

function calcValue(value: number, min: number, max: number, step: number) {
  if (step <= 0) return value
  const clamped = clamp(value, min, max)
  return Math.round((clamped - min) / step) * step + min
}

function calcNewValueOnKeyboardNavigation(
  key: string,
  val: number,
  stepNum: number,
  minNum: number,
  maxNum: number
): number | null {
  switch (key) {
    case Keys.Left:
    case Keys.Down:
      return calcValue(val - stepNum, minNum, maxNum, stepNum)
    case Keys.Right:
    case Keys.Up:
      return calcValue(val + stepNum, minNum, maxNum, stepNum)
    case Keys.Home:
    case Keys.End:
      return calcValue(maxNum, minNum, maxNum, stepNum)
    default:
      return val
  }
}

const RangeSlider = forwardRef<HTMLInputElement, RangeSliderProps>(
  (
    {
      className,
      trackClassName,
      thumbClassName,
      trackFilledClassName,
      min = 0,
      max = 100,
      step = 1,
      value,
      defaultValue,
      onChange,
      'disabled': disabled_,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'children': Content = () => null,
      scaleFormat,
      ...rest
    },
    ref
  ) => {
    const isControlled = value !== undefined
    const [mounted, setMounted] = useState(false)
    const disabled = disabled_ || !mounted
    const minNum = Number(min)
    const maxNum = Number(max)
    const stepNum = Number(step)

    useEffect(() => {
      setMounted(true)
    }, [])

    // Snap initial value to step
    const getInitialValue = useCallback(() => {
      let v: number
      if (typeof value === 'number') v = value
      else if (typeof defaultValue === 'number') v = defaultValue
      else if (typeof value === 'string') v = Number(value)
      else if (typeof defaultValue === 'string') v = Number(defaultValue)
      else v = minNum
      return calcValue(v, minNum, maxNum, stepNum)
    }, [value, defaultValue, minNum, maxNum, stepNum])

    const [internalValue, setInternalValue] = useState<number>(getInitialValue())

    // Keep internal value in sync with controlled value, snapping to step
    useEffect(() => {
      if (isControlled && typeof value !== 'undefined' && !Array.isArray(value)) {
        const v = typeof value === 'string' ? Number(value) : (value as number)
        setInternalValue(calcValue(v, minNum, maxNum, stepNum))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, isControlled, minNum, maxNum, stepNum])

    const sliderRef = useRef<HTMLDivElement>(null)
    const hiddenInputRef = useRef<HTMLInputElement>(null)

    const updateSliderRectRef = useCallback(() => {
      const slider = sliderRef.current
      if (!slider) return
      sliderRectRef.current = slider.getBoundingClientRect()
    }, [])

    // Expose the hidden input ref, and allow external value changes to update the slider
    useImperativeHandle(ref, () => {
      return {
        ...(hiddenInputRef.current as HTMLInputElement),
        set value(newValue: string) {
          if (hiddenInputRef.current) {
            hiddenInputRef.current.value = newValue
            const snappedValue = calcValue(Number(newValue), minNum, maxNum, stepNum)
            if (!isControlled) setInternalValue(snappedValue)
            // Fire onChange if present
            if (onChange) {
              const event = new Event('change', { bubbles: true })
              Object.defineProperty(event, 'target', { writable: false, value: hiddenInputRef.current })
              onChange(event as unknown as React.ChangeEvent<HTMLInputElement>)
            }
          }
        },
        get value() {
          return hiddenInputRef.current?.value ?? ''
        },
      } as unknown as HTMLInputElement
    })

    // Listen for direct changes to the hidden input's value via ref
    useEffect(() => {
      const input = hiddenInputRef.current
      if (!input) return

      const observer = new MutationObserver(() => {
        const snappedValue = calcValue(Number(input.value), minNum, maxNum, stepNum)
        if (!isControlled) setInternalValue(snappedValue)
      })

      observer.observe(input, { attributes: true, attributeFilter: ['value'] })

      return () => observer.disconnect()
    }, [isControlled, minNum, maxNum, stepNum])

    // Calculate thumb position
    const val = calcValue(internalValue, minNum, maxNum, stepNum)
    const [position, setPosition] = useState<number | undefined>(undefined)
    const sliderRectRef = useRef<DOMRect | undefined>(undefined)
    const percent = getPercent(val, minNum, maxNum)

    useEffect(() => {
      updateSliderRectRef()
    }, [updateSliderRectRef])

    // Drag state
    const [dragging, setDragging] = useState(false)
    const getPercentFromPosition = useCallback((clientX: number) => {
      const rect = sliderRectRef.current
      if (!rect) return 0
      let x = clientX - rect.left
      x = clamp(x, 0, rect.width)
      return x / rect.width
    }, [])

    // Calculate value from mouse/touch position, snapping to step
    const getValueFromPosition = useCallback(
      (clientX: number) => {
        const percent = getPercentFromPosition(clientX)
        const newValue = minNum + percent * (maxNum - minNum)
        return calcValue(newValue, minNum, maxNum, stepNum)
      },
      [getPercentFromPosition, minNum, maxNum, stepNum]
    )

    const rawPercent = position !== undefined ? getPercentFromPosition(position) * 100 : undefined

    // Handle pointer events
    const handlePointerDown = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (disabled || !hiddenInputRef.current) return
        updateSliderRectRef()
        setDragging(true)
        try {
          ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
        } catch {}
        const clientX = e.clientX
        const newValue = getValueFromPosition(clientX)
        hiddenInputRef.current.value = newValue.toString()
        if (!isControlled) setInternalValue(newValue)
        if (onChange) {
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              value: newValue,
            },
          } as unknown as React.ChangeEvent<HTMLInputElement>
          onChange(syntheticEvent)
        }
      },
      [disabled, getValueFromPosition, isControlled, onChange, updateSliderRectRef]
    )

    const handlePointerMove = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragging || disabled) return
        const clientX = e.clientX
        window.requestAnimationFrame(() => {
          setPosition(clientX)
          const newValue = getValueFromPosition(clientX)
          if (!isControlled) setInternalValue(newValue)
          if (!onChange) return
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              value: newValue,
            },
          } as unknown as React.ChangeEvent<HTMLInputElement>
          onChange(syntheticEvent)
        })
      },
      [dragging, disabled, getValueFromPosition, isControlled, onChange]
    )

    const handlePointerUp = useCallback(() => {
      setDragging(false)
      setPosition(undefined)
    }, [setDragging])

    // Keyboard support
    const handleThumbKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return
      const newValue = calcNewValueOnKeyboardNavigation(e.key, val, stepNum, minNum, maxNum)
      if (newValue === null) return
      e.preventDefault()
      if (!isControlled) setInternalValue(newValue)
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: newValue,
          },
        } as unknown as React.ChangeEvent<HTMLInputElement>
        onChange(syntheticEvent)
      }
    }

    // Click on track
    const handleTrackClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (disabled) return
        updateSliderRectRef()
        const rect = sliderRectRef.current
        if (!rect) return
        const x = e.clientX - rect.left
        const percent = x / rect.width
        let newValue = minNum + percent * (maxNum - minNum)
        newValue = calcValue(newValue, minNum, maxNum, stepNum)
        if (!isControlled) setInternalValue(newValue)
        if (onChange) {
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              value: newValue,
            },
          } as unknown as React.ChangeEvent<HTMLInputElement>
          onChange(syntheticEvent)
        }
      },
      [disabled, updateSliderRectRef, minNum, maxNum, stepNum, isControlled, onChange]
    )

    const displayPercent = dragging ? (rawPercent ?? percent) : percent

    return (
      <LayoutStack
        className={classNames(styles.rangeSlider, className, { [styles.dragging as string]: dragging })}
        aria-disabled={disabled}
        tabIndex={-1}
        space="100"
      >
        <LayoutCluster className={styles.sliderContainer}>
          <div
            ref={sliderRef}
            className={classNames(styles.track, trackClassName)}
            onClick={handleTrackClick}
            role="presentation"
          >
            <div
              className={classNames(styles.filledTrack, trackFilledClassName)}
              style={{ inlineSize: `${displayPercent}%` }}
            />
          </div>
          <div
            className={classNames(styles.thumb, thumbClassName)}
            style={{ insetInlineStart: `${displayPercent}%` }}
            role="slider"
            tabIndex={disabled ? -1 : 0}
            aria-valuenow={val}
            aria-valuemin={minNum}
            aria-valuemax={maxNum}
            aria-disabled={disabled}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            onPointerDown={handlePointerDown}
            onKeyDown={handleThumbKeyDown}
            onPointerUp={handlePointerUp}
            onPointerMove={handlePointerMove}
            {...rest}
          >
            <Content value={val} percent={displayPercent} />
          </div>
        </LayoutCluster>
        {typeof scaleFormat === 'function' ? (
          <dl className={styles.scale}>
            <dd className={classNames(styles.scaleValue, styles.scaleValueFrom)}>{scaleFormat(minNum)}</dd>
            <dd className={classNames(styles.scaleValue, styles.scaleValueTo)}>{scaleFormat(maxNum)}</dd>
          </dl>
        ) : null}
        {/* Hidden input for ref forwarding */}
        <input type="hidden" ref={hiddenInputRef} value={val} readOnly tabIndex={-1} name={rest.name} />
      </LayoutStack>
    )
  }
)

RangeSlider.displayName = 'RangeSlider'

export default RangeSlider
